<template>
  <div
    class="ig-upload"
    :class="uploadClasses"
    @dragover.prevent="onDragOver"
    @dragenter.prevent="onDragEnter"
    @dragleave.prevent="onDragLeave"
    @drop.prevent="onDrop"
    @paste="onPaste"
  >
    <input
      ref="fileInput"
      type="file"
      :multiple="multiple"
      accept="image/*"
      class="ig-upload__file-input"
      @change="onFileChange"
    >

    <div
      class="ig-upload__drop"
      :class="dropClasses"
      @click="$refs.fileInput.click()"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="ig-upload__icon"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line
          x1="12"
          x2="12"
          y1="3"
          y2="15"
        />
      </svg>
      <p class="ig-upload__text">
        Drag &amp; drop images here, paste from clipboard, or
        <button
          type="button"
          class="ig-upload__browse"
          @click.stop="$refs.fileInput.click()"
        >
          browse
        </button>
      </p>
    </div>

    <div
      v-if="images.length"
      class="ig-upload__grid"
    >
      <div
        v-for="item in images"
        :key="item._localId"
        class="ig-upload__thumb group/img"
      >
        <img
          :src="item._preview || imageUrl(item)"
          alt=""
          class="ig-upload__img"
        >
        <button
          type="button"
          class="ig-upload__remove"
          title="Remove image"
          @click.stop="$emit('remove', item)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>
    </div>

    <p
      v-if="error"
      class="ig-upload__error"
      role="alert"
    >
      {{ error }}
    </p>
  </div>
</template>

<script>
import { imageUrl } from './image-group-utils';

export default {
  name: 'ImageGroupUploadZone',
  props: {
    images: { type: Array, default: () => [] },
    error: { type: String, default: '' },
    multiple: { type: Boolean, default: true },
    variant: {
      type: String,
      default: 'default',
      validator: (v) => ['default', 'lovable'].includes(v),
    },
  },
  data() {
    return {
      isDragging: false,
      dragDepth: 0,
    };
  },
  computed: {
    uploadClasses() {
      return {
        'ig-upload--lovable': this.variant === 'lovable',
        'ig-upload--active': this.isDragging,
      };
    },
    dropClasses() {
      return {
        'ig-upload__drop--bordered': this.variant === 'lovable',
        'ig-upload__drop--error': !!this.error,
      };
    },
  },
  methods: {
    imageUrl,
    onDragOver() {
      this.isDragging = true;
    },
    onDragEnter() {
      this.dragDepth += 1;
      this.isDragging = true;
    },
    onDragLeave() {
      this.dragDepth = Math.max(0, this.dragDepth - 1);
      if (this.dragDepth === 0) {
        this.isDragging = false;
      }
    },
    onDrop(e) {
      this.dragDepth = 0;
      this.isDragging = false;
      const files = Array.from(e.dataTransfer?.files || []);
      this.emitFiles(files);
    },
    onFileChange(e) {
      const files = Array.from(e.target.files || []);
      this.emitFiles(files);
      e.target.value = '';
    },
    onPaste(e) {
      const items = Array.from(e.clipboardData?.items || []);
      const files = items
        .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
        .map((item) => item.getAsFile())
        .filter(Boolean);
      if (files.length) {
        e.preventDefault();
        this.emitFiles(files);
      }
    },
    emitFiles(files) {
      const images = files.filter((f) => f && f.type && f.type.startsWith('image/'));
      if (images.length) {
        this.$emit('add-files', this.multiple ? images : images.slice(0, 1));
      }
    },
  },
};
</script>

<style scoped lang="scss">
$ig-primary: #209c86;
$ig-muted: #6b7280;
$ig-border: #e5e7eb;

.ig-upload {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ig-upload--lovable {
  gap: 12px;
}

.ig-upload:not(.ig-upload--lovable) {
  border: 1px dashed #d1d5db;
  border-radius: 8px;
  padding: 16px;
  background: #fafafa;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.ig-upload--active:not(.ig-upload--lovable) {
  border-color: #1abc9c;
  background: rgba(26, 188, 156, 0.06);
}

.ig-upload__file-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

.ig-upload__drop {
  text-align: center;
}

.ig-upload__drop--bordered {
  border: 2px dashed $ig-border;
  border-radius: 8px;
  padding: 20px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.ig-upload__drop--bordered:hover:not(.ig-upload__drop--error) {
  background: rgba(0, 0, 0, 0.02);
  border-color: #c9cdd4;
}

.ig-upload--lovable.ig-upload--active .ig-upload__drop--bordered:not(.ig-upload__drop--error) {
  border-color: $ig-primary;
  background: rgba(32, 156, 134, 0.04);
}

.ig-upload__drop--error {
  border-color: #dc2626 !important;
  background: #fef2f2 !important;
}

.ig-upload__error {
  font-size: 13px;
  line-height: 1.4;
  color: #dc2626;
  margin: 0;
}

.ig-upload__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 80px));
  gap: 8px;
  justify-content: flex-start;
}

.ig-upload__thumb {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  background: #f3f4f6;
  border: 1px solid $ig-border;
}

.ig-upload__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.ig-upload__remove {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 2;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s ease;
  color: #111827;

  svg {
    width: 12px;
    height: 12px;
  }
}

.group\/img:hover .ig-upload__remove {
  opacity: 1;
}

.ig-upload__icon {
  width: 20px;
  height: 20px;
  color: var(--text-muted, #9ca3af);
  margin: 0 auto 8px;
  display: block;
}

.ig-upload__text {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.5;
  color: var(--text-primary, #1a1d23);
  margin: 0;
}

.ig-upload__browse {
  border: none;
  background: none;
  padding: 0;
  color: $ig-primary;
  font-size: inherit;
  font-weight: inherit;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: #1a8a76;
  }
}
</style>
